import { createContext, useEffect, useState, ReactNode, useContext } from "react";
import { setSession } from "src/api/api";
import jwtDecode from "jwt-decode";
import {
  signIn,
  signInWithGoogle,
  signInWithOtp,
  verify,
} from "src/api/publicRoutes";
import { getAdminProfile } from "src/api/adminRoutes";
import { ThemeModeContext } from "./ThemeContext";
import { useNavigate } from "react-router-dom";
import { isValidToken } from "src/utils/utils";

export interface AuthContextInterface {
  role: string;
  uid: string;
  username: string;
  first_name: string;
  last_name: string;
  timezone: string;
  theme: 'light' | 'dark';
}

const defaultState: AuthContextInterface = {
  role: "",
  uid: "",
  username: "",
  first_name: "",
  last_name: "",
  timezone: "",
  theme: 'light',
};

export const AuthContext = createContext({
  ...defaultState,
  login: (user: string, password: string, browser: string) =>
    Promise.resolve({ success: false, message: null }),
  loginWithOtp: (user: string, otp: string, browser: string) =>
    Promise.resolve({ success: false, message: null }),
  loginWithGoogle: (
    user: string,
    jwt: string,
    name: string,
    browser: string,
    lang: string,
    country: string,
  ) => Promise.resolve({ success: false, message: null }),
  logout: () => {},
  verifyAccount: (token, browser) => Promise.resolve({ success: false }),
});

let logoutFunction: () => void;

export interface AuthProviderProps {
  children: ReactNode;
  setLanguage: (lang: string) => void;
}

export const AuthProvider = ({ children, setLanguage }: AuthProviderProps) => {
  const navigate = useNavigate();
  const { setMode } = useContext(ThemeModeContext);

  const logout = () => {
    setState(defaultState);
    setSession(null);
    setMode('light');
    navigate("/signin");
  };

  logoutFunction = logout;

  const login = async (user: string, password: string, browser: string) => {
    const response = await signIn(user, password, browser);
    if (response.success) {
      const token = response.data.token;

      const jwt = jwtDecode(token) as {
        role: string;
        uid: string;
        username: string;
        first_name: string;
        last_name: string;
        lang: string;
        timezone: string;
        theme: 'light' | 'dark';
      };
      let theme = jwt.theme;
      if (!theme) {
        const profileRes = await getAdminProfile(jwt.uid);
        if (profileRes.success) {
          theme = profileRes.data[0].theme;
        }
      }
      if (token && isValidToken(token)) {
        setState({ ...jwt, theme });
      }

      setMode(theme);
      setLanguage(jwt.lang);
    }

    return { success: response.success, message: response.message };
  };

  const loginWithOtp = async (user: string, otp: string, browser: string) => {
    const response = await signInWithOtp(user, otp, browser);
    if (response.success) {
      const token = response.data.token;

      const jwt = jwtDecode(token) as {
        role: string;
        uid: string;
        username: string;
        first_name: string;
        last_name: string;
        lang: string;
        timezone: string;
        theme: 'light' | 'dark';
      };
      let theme = jwt.theme;
      if (!theme) {
        const profileRes = await getAdminProfile(jwt.uid);
        if (profileRes.success) {
          theme = profileRes.data[0].theme;
        }
      }
      if (token && isValidToken(token)) {
        setState({ ...jwt, theme });
      }

      setMode(theme);
      setLanguage(jwt.lang);
    }

    return { success: response.success, message: response.message };
  };

  const verifyAccount = async (token: string, browser: string) => {
    const response = await verify(token, browser);
    if (response.success) {
      const token = response.data.token;

      const jwt = jwtDecode(token) as {
        role: string;
        uid: string;
        username: string;
        first_name: string;
        last_name: string;
        lang: string;
        timezone: string;
        theme: 'light' | 'dark';
      };
      let theme = jwt.theme;
      if (!theme) {
        const profileRes = await getAdminProfile(jwt.uid);
        if (profileRes.success) {
          theme = profileRes.data[0].theme;
        }
      }
      if (token && isValidToken(token)) {
        setState({ ...jwt, theme });
      }

      setMode(theme);
      setLanguage(jwt.lang);
    }
    return { success: response.success };
  };

  const loginWithGoogle = async (
    user: string,
    jwt: string,
    name: string,
    browser: string,
    lang: string,
    country: string,
  ) => {
    const response = await signInWithGoogle(
      user,
      jwt,
      name,
      browser,
      lang,
      country,
    );
    if (response.success) {
      const token = response.data.token;

      const jwt = jwtDecode(token) as {
        role: string;
        uid: string;
        username: string;
        name: string;
        first_name: string;
        last_name: string;
        lang: string;
        timezone: string;
        theme: 'light' | 'dark';
      };
      let theme = jwt.theme;
      if (!theme) {
        const profileRes = await getAdminProfile(jwt.uid);
        if (profileRes.success) {
          theme = profileRes.data[0].theme;
        }
      }
      if (token && isValidToken(token)) {
        setState({ ...jwt, theme });
      }

      setMode(theme);
      setLanguage(jwt.lang);
    }

    return { success: response.success, message: response.message };
  };

  const token = window.localStorage.getItem("accessToken");
  const jwt:
    | {
        role: string;
        uid: string;
        username: string;
        first_name: string;
        last_name: string;
        lang: string;
        timezone: string;
        theme: 'light' | 'dark';
      }
    | undefined = !token ? undefined : jwtDecode(token);

  useEffect(() => {
    const fetchToken = async () => {
      if (!token) {
        return;
      }

      if (token && isValidToken(token)) {
        setSession(token);
      }

      let theme = jwt.theme;
      if (!theme) {
        const profileRes = await getAdminProfile(jwt.uid);
        if (profileRes.success) {
          theme = profileRes.data[0].theme;
        }
      }

      setLanguage(jwt.lang);
    };
    fetchToken();
  }, [setLanguage, jwt, token]);

  const [state, setState] = useState<AuthContextInterface>(
    jwt ? jwt : defaultState,
  );

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        loginWithOtp,
        loginWithGoogle,
        logout,
        verifyAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const signout = () => {
  return logoutFunction();
};
